import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.string.includes.js";
import "core-js/modules/es.array.find.js";
import "core-js/modules/es.array.map.js";
import "core-js/modules/es.array.iterator.js";
import "core-js/modules/es.set.js";
import "core-js/modules/es.string.iterator.js";
import "core-js/modules/web.dom-collections.iterator.js";
import "core-js/modules/web.dom-collections.for-each.js";
import { useEffect, useMemo, useRef } from 'react';
import { flattenTree } from 'utils/tree';
import useSelectionQuery from 'hooks/useSelectionQuery';
import useRowSelection from 'hooks/useRowSelection';
import { getSectionContentMap } from './utils/getSectionContentMap';
export function useContentManagerSelection(_ref) {
  var content = _ref.content,
    contentAsTree = _ref.contentAsTree;
  // The source of truth for the selection is stored in the
  // URL query parameter, e.g. `?selection=33`
  var _useSelectionQuery = useSelectionQuery(),
    _useSelectionQuery2 = _slicedToArray(_useSelectionQuery, 2),
    selectedItemIds = _useSelectionQuery2[0],
    setSelection = _useSelectionQuery2[1];

  // All selected content items
  var selectedItems = useMemo(function () {
    return content.filter(function (item) {
      return selectedItemIds.includes(item.id);
    });
  }, [selectedItemIds, content]);

  // If the URL selection includes an item that was since removed from
  // the editor, we update the selection to not include it anymore
  useEffect(function () {
    var isSelectedItemMissing = selectedItemIds.some(function (id) {
      return !selectedItems.find(function (item) {
        return item.id === id;
      });
    });
    if (isSelectedItemMissing) {
      setSelection(selectedItems.map(function (item) {
        return item.id;
      }));
    }
  }, [selectedItemIds, selectedItems, setSelection]);

  // A map of all section ids to an array of ids of their children
  var sectionContentMap = useMemo(function () {
    return getSectionContentMap(content);
  }, [content]);

  // This function for setting the selection ensures that when a
  // section is selected, none of its children are selected.
  // This helps determine whether we're dealing with a single selection.
  function setSelectedItemIds(newSelection) {
    var childrenIds = new Set();
    newSelection.forEach(function (id) {
      if (sectionContentMap.has(id)) {
        var children = sectionContentMap.get(id) || [];
        children.forEach(function (id) {
          return childrenIds.add(id);
        });
      }
    });
    setSelection(newSelection.filter(function (id) {
      return !childrenIds.has(id);
    }));
  }
  function clearSelection() {
    setSelection([]);
  }

  // A flat, sorted list of all playlist content to enable range selection
  var sortedItemIds = useMemo(function () {
    // `contentAsTree` is sorted, so we just need to flatten it
    // to get a sorted list of all content
    return flattenTree(contentAsTree).map(function (item) {
      return item.id;
    });
  }, [contentAsTree]);

  // `useRowSelection` requires the index of the last focused/hovered item.
  // Since this isn't exposed in the UI, we store it in a ref.
  var highlightedItemIndexRef = useRef(0);
  function getHighlightRowHandler(itemId) {
    return function () {
      highlightedItemIndexRef.current = sortedItemIds.indexOf(itemId);
    };
  }

  // `useRowSelection` handles keyboard shortcuts such as Ctrl+A
  // to select all, and range selection when Shift is held
  var _useRowSelection = useRowSelection({
      rowIds: sortedItemIds,
      highlightedRowIndex: highlightedItemIndexRef,
      selectedItemIds: selectedItemIds,
      allowTogglingSingleSelection: true,
      onChange: setSelectedItemIds
    }),
    getSelectRowHandler = _useRowSelection.getSelectRowHandler,
    getSelectSingleRowHandler = _useRowSelection.getSelectSingleRowHandler,
    handleGridSelectionKeys = _useRowSelection.handleGridSelectionKeys;
  return {
    hasSelectedItems: selectedItemIds.length > 0,
    selectedItemIds: selectedItemIds,
    selectedItems: selectedItems,
    clearSelection: clearSelection,
    getSelectRowHandler: getSelectRowHandler,
    getHighlightRowHandler: getHighlightRowHandler,
    getSelectSingleRowHandler: getSelectSingleRowHandler,
    handleGridSelectionKeys: handleGridSelectionKeys
  };
}